"use client"

import { useTranslations } from "next-intl"
import { motion, useReducedMotion } from "framer-motion"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { Button } from "@/components/ui/button"
import { useMobile } from "@/hooks/use-mobile"

const FEED_MOTION_EASE = [0.4, 0, 0.2, 1] as const
const FEED_MOTION_MS = 0.25

type OfferAutoDeclinedSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function OfferAutoDeclinedSheet({ open, onOpenChange }: OfferAutoDeclinedSheetProps) {
  const t = useTranslations("offers.autoDeclined")
  const tErr = useTranslations("offers.errors")
  const isMobile = useMobile()
  const reduceMotion = useReducedMotion()
  const innerEase = {
    duration: reduceMotion ? 0.01 : FEED_MOTION_MS,
    ease: FEED_MOTION_EASE,
  }
  const innerUp = reduceMotion
    ? false
    : ({ opacity: 0, y: 6 } as { opacity: number; y: number })
  const bodyText = (
    <div className="w-full text-center">
      <p className="text-balance text-sm leading-relaxed text-foreground/85 sm:text-sm">
        {tErr("belowMinimumInline", {
          default: "Your offer is below the seller's minimum and was auto-declined. Try a higher amount.",
        })}
      </p>
    </div>
  )

  const primaryCta = (
    <Button
      type="button"
      className="h-12 w-full rounded-full bg-amber-600 text-base font-medium text-white hover:bg-amber-600/90"
      onClick={() => onOpenChange(false)}
    >
      {t("cta", { default: "OK" })}
    </Button>
  )

  if (!isMobile) {
    return (
      <DialogCard
        open={open}
        onOpenChange={onOpenChange}
        nested
        title={t("title", { default: "Offer too low" })}
        panelClassName="max-w-md max-h-[min(90dvh,720px)]"
        footer={primaryCta}
      >
        {bodyText}
      </DialogCard>
    )
  }

  return (
    <DockSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("title", { default: "Offer too low" })}
      footer={primaryCta}
    >
      <motion.div
        className="w-full text-center"
        initial={innerUp}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...innerEase, delay: reduceMotion ? 0 : 0.04 }}
      >
        {bodyText}
      </motion.div>
    </DockSheet>
  )
}
